import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Table, Badge, Row, Col, Spinner, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import './GlobalScheduling.css';
import InterviewDetailsModal from '../../components/admin/InterviewDetailsModal';
import { getAllCompaniesPanels } from '../../api/companyApi';
import { getGlobalSchedule, scheduleInterviews } from '../../api/schedulingApi';

const GlobalSchedulingPage = () => {
  // Load saved state from localStorage
  const loadFromLocalStorage = (key, defaultValue) => {
    try {
      const saved = localStorage.getItem(`globalScheduling_${key}`);
      if (saved !== null) {
        if (key === 'selectedDate') {
          return new Date(saved);
        }
        if (key === 'selectedCompanies') {
          return JSON.parse(saved);
        }
        return saved;
      }
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
    }
    return defaultValue;
  };

  const [selectedDate, setSelectedDate] = useState(() => loadFromLocalStorage('selectedDate', new Date()));
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState(() => loadFromLocalStorage('selectedCompanies', []));
  const [timeSlots, setTimeSlots] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [unscheduledStudents, setUnscheduledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(() => loadFromLocalStorage('viewMode', 'compact')); // 'compact' or 'detailed'
  const [conflicts, setConflicts] = useState([]);
  const [studentFilter, setStudentFilter] = useState(() => loadFromLocalStorage('studentFilter', ''));
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      // Auto-select all companies when date changes
      const companiesForDate = getCompaniesForDate();
      if (companiesForDate.length > 0) {
        // Check if we have saved selections for this date
        const savedCompanies = loadFromLocalStorage('selectedCompanies', []);
        const validSavedCompanies = savedCompanies.filter(id => 
          companiesForDate.some(company => company.id.toString() === id)
        );
        
        if (validSavedCompanies.length > 0) {
          // Use saved selections if they're valid for this date
          setSelectedCompanies(validSavedCompanies);
        } else {
          // Otherwise select all companies
          const allCompanyIds = companiesForDate.map(company => company.id.toString());
          setSelectedCompanies(allCompanyIds);
          localStorage.setItem('globalScheduling_selectedCompanies', JSON.stringify(allCompanyIds));
        }
      }
    }
  }, [selectedDate, companies]);
  
  useEffect(() => {
    if (selectedDate && selectedCompanies.length > 0) {
      fetchScheduleData();
    }
  }, [selectedDate, selectedCompanies]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllCompaniesPanels();
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to fetch companies. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduleData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      // Call the global schedule API instead of individual company schedules
      const response = await getGlobalSchedule(dateStr, selectedCompanies);
      
      setInterviews(response.interviews || []);
      setUnscheduledStudents(response.unscheduled_interviews || []);
      setConflicts(response.conflicts || []);
      
      // Generate time slots from the actual interviews instead of guessing
      generateTimeSlotsFromInterviews(response.interviews || []);
      
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setError('Failed to fetch schedule data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const generateTimeSlotsFromInterviews = (interviews) => {
    // Extract unique time slots from interviews
    const uniqueSlots = new Map();
    
    interviews.forEach(interview => {
      if (interview.time_slot && interview.time_slot.start_time && interview.time_slot.end_time) {
        const key = `${interview.time_slot.start_time}-${interview.time_slot.end_time}`;
        if (!uniqueSlots.has(key)) {
          uniqueSlots.set(key, {
            startTime: interview.time_slot.start_time,
            endTime: interview.time_slot.end_time
          });
        }
      }
    });
    
    // Convert map to array and sort by start time
    const slots = Array.from(uniqueSlots.values()).sort((a, b) => {
      return a.startTime.localeCompare(b.startTime);
    });
    
    setTimeSlots(slots);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    localStorage.setItem('globalScheduling_selectedDate', date.toISOString());
  };
  
  const handleStudentFilterChange = (e) => {
    const value = e.target.value;
    setStudentFilter(value);
    localStorage.setItem('globalScheduling_studentFilter', value);
  };
  
  const handleInterviewClick = (interview) => {
    setSelectedInterview(interview);
    setShowInterviewModal(true);
  };

  const handleGenerateSchedule = async () => {
    try {
      setGenerating(true);
      setError('');
      
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      // Call API to generate schedule
      const response = await scheduleInterviews(dateStr);
      
      if (response.stats) {
        toast.success(`Generated schedule with ${response.stats.total_scheduled} interviews`);
        toast.info(`${response.stats.total_unscheduled} students could not be scheduled`);
        
        // Refresh schedule data
        fetchScheduleData();
      }
    } catch (error) {
      console.error('Error generating schedule:', error);
      setError('Failed to generate schedule. Please try again later.');
      toast.error('Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const getInterviewsForTimeSlot = (timeSlot, companyId) => {
    let filteredInterviews = interviews.filter(interview => 
      interview.company.id === parseInt(companyId) &&
      interview.time_slot.start_time === timeSlot.startTime
    );
    
    // Apply student filter if provided
    if (studentFilter) {
      filteredInterviews = filteredInterviews.filter(interview => 
        interview.student.email.toLowerCase().includes(studentFilter.toLowerCase()) ||
        interview.student.name.toLowerCase().includes(studentFilter.toLowerCase())
      );
    }
    
    return filteredInterviews;
  };

  const getCompaniesForDate = () => {
    if (!selectedDate) return [];
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    return companies.filter(company => company.visit_date === dateStr);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.includes(':') ? 
      new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }) : 
      timeString;
  };

  const renderInterviewCell = (timeSlot, companyId) => {
    const interviewsInSlot = getInterviewsForTimeSlot(timeSlot, companyId);
    
    if (interviewsInSlot.length === 0) {
      return <div className="empty-cell"></div>;
    }
    
    // Check if this cell matches the student filter
    const matchesFilter = studentFilter && interviewsInSlot.some(interview => 
      interview.student.email.toLowerCase().includes(studentFilter.toLowerCase()) ||
      interview.student.name.toLowerCase().includes(studentFilter.toLowerCase())
    );
    
    // Compact view just shows count and color indicator
    if (viewMode === 'compact') {
      const hasConflict = interviewsInSlot.some(interview => 
        conflicts.some(conflict => 
          conflict.interviews.some(i => i.id === interview.id)
        )
      );
      
      return (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip className="schedule-tooltip">
              {interviewsInSlot.map(interview => (
                <div key={interview.id}>
                  <strong>{interview.student.name}</strong> - Panel {interview.panel.number}<br/>
                  <small>{interview.panel.interviewer}</small>
                </div>
              ))}
            </Tooltip>
          }
        >
          <div 
            className={`interview-cell ${hasConflict ? 'conflict-cell' : 'student-interview'} ${matchesFilter ? 'student-highlight' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => handleInterviewClick(interviewsInSlot)}
          >
            {interviewsInSlot.length}
          </div>
        </OverlayTrigger>
      );
    }
    
    // Detailed view shows student names
    return (
      <div 
        style={{ cursor: 'pointer' }} 
        onClick={() => handleInterviewClick(interviewsInSlot)}
        className={matchesFilter ? 'student-highlight p-1 rounded' : ''}
      >
        {interviewsInSlot.map(interview => (
          <div 
            key={interview.id}
            className={conflicts.some(conflict => 
              conflict.interviews.some(i => i.id === interview.id)
            ) ? 'text-danger' : ''}
            style={{ fontSize: '0.8rem' }}
          >
            {interview.student.email.split('@')[0]}
            <span className="text-muted ms-1">P{interview.panel.number}</span>
          </div>
        ))}
      </div>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };
  
  // Get unscheduled students for a specific company
  const getUnscheduledForCompany = (companyId) => {
    return unscheduledStudents.filter(item => item.company.id === parseInt(companyId));
  };
  
  return (
    <Container fluid className="py-4">
      <h1 className="mb-4">Global Scheduling View</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Header as="h5">Select Companies to View</Card.Header>
        <Card.Body>
      
          <Row className="align-items-end">
            <Col md={2}>
              <Form.Group className="mb-1">
                <Form.Label>Visit Date</Form.Label>
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  className="form-control"
                  dateFormat="yyyy-MM-dd"
                />
              </Form.Group>
   
            </Col>
            <Col md={1}>

            {getCompaniesForDate().length === 0 ? (
            <Alert variant="info">
              No companies scheduled for the selected date.
            </Alert>
          ) : (
            <div className="text-center mt-1">
              <Button 
                variant="primary" 
                onClick={handleGenerateSchedule}
                disabled={generating || loading}
              >
                {generating ? (
                  <>
                    <Spinner animation="border" size="sm" className="m-1" />
                    Generating Schedule...
                  </>
                ) : 'Generate Schedule'}
              </Button>
            </div>
          )}
          </Col>
            <Col md={4}>
              <Form.Group className="mb-1">
                <Form.Label>Companies</Form.Label>
                <Card className="company-checkbox-container" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                  <Card.Body className="p-2">
                    <Form.Check
                      type="checkbox"
                      label="Select All Companies"
                      checked={selectedCompanies.length === getCompaniesForDate().length}
                      onChange={() => {
                        let newSelection;
                        if (selectedCompanies.length === getCompaniesForDate().length) {
                          newSelection = [];
                        } else {
                          newSelection = getCompaniesForDate().map(c => c.id.toString());
                        }
                        setSelectedCompanies(newSelection);
                        localStorage.setItem('globalScheduling_selectedCompanies', JSON.stringify(newSelection));
                      }}
                      className="mb-2 fw-bold border-bottom pb-2"
                    />
                    
                    {getCompaniesForDate().map(company => (
                      <Form.Check
                        key={company.id}
                        type="checkbox"
                        id={`company-${company.id}`}
                        label={company.name}
                        checked={selectedCompanies.includes(company.id.toString())}
                        onChange={() => {
                          let newSelection;
                          if (selectedCompanies.includes(company.id.toString())) {
                            newSelection = selectedCompanies.filter(id => id !== company.id.toString());
                          } else {
                            newSelection = [...selectedCompanies, company.id.toString()];
                          }
                          setSelectedCompanies(newSelection);
                          localStorage.setItem('globalScheduling_selectedCompanies', JSON.stringify(newSelection));
                        }}
                        className="mb-1"
                      />
                    ))}
                  </Card.Body>
                </Card>
              </Form.Group>
            </Col>
            
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>View Mode</Form.Label>
                <div>
                  <Form.Check
                    inline
                    label="Compact"
                    name="viewMode"
                    type="radio"
                    id="compact-view"
                    checked={viewMode === 'compact'}
                    onChange={() => {
                      setViewMode('compact');
                      localStorage.setItem('globalScheduling_viewMode', 'compact');
                    }}
                  />
                  <Form.Check
                    inline
                    label="Detailed"
                    name="viewMode"
                    type="radio"
                    id="detailed-view"
                    checked={viewMode === 'detailed'}
                    onChange={() => {
                      setViewMode('detailed');
                      localStorage.setItem('globalScheduling_viewMode', 'detailed');
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
                <Form.Group className="mb-3">
                <Form.Label>Filter by Student</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="text"
                    placeholder="Enter email or name"
                    value={studentFilter}
                    onChange={handleStudentFilterChange}
                  />
                  {studentFilter && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => {
                        setStudentFilter('');
                        localStorage.setItem('globalScheduling_studentFilter', '');
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <Form.Text className="text-muted">
                  {studentFilter ? 
                    `Filtering for "${studentFilter}"` : 
                    "Enter student email or name to filter interviews"
                  }
                </Form.Text>
              </Form.Group>
        </Card.Body>
        
      </Card>
      
      {selectedCompanies.length > 0 ? (
        <>
          <Card className="mb-4">
            <Card.Header as="h5" className="d-flex justify-content-between">
              <span>Global Schedule View</span>
              <span>
                {conflicts.length > 0 && (
                  <Badge bg="danger">
                    {conflicts.length} Conflicts Found
                  </Badge>
                )}
              </span>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status" variant="primary" />
                  <p className="mt-2">Loading schedule...</p>
                </div>
              ) : timeSlots.length === 0 ? (
                <Alert variant="warning">No schedule data available.</Alert>
              ) : (
                <div className={`schedule-container table-responsive view-${viewMode}`}>
                  <Table bordered className="global-schedule-table">
                    <thead>
                      <tr>
                        <th width="80">Time</th>
                        {selectedCompanies.map(companyId => {
                          const company = companies.find(c => c.id === parseInt(companyId));
                          return (
                            <th key={companyId} className="text-center">
                              {company?.name}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map((timeSlot, i) => (
                        <tr key={`slot-${i}`}>
                          <td className="text-center align-middle" style={{ whiteSpace: 'nowrap' }}>
                            {formatTime(timeSlot.startTime)}
                            {viewMode !== 'compact' && (
                              <>
                                <br />
                                <small className="text-muted">to</small>
                                <br />
                                {formatTime(timeSlot.endTime)}
                              </>
                            )}
                          </td>
                          
                          {selectedCompanies.map(companyId => (
                            <td 
                              key={`slot-${i}-company-${companyId}`} 
                              className={`${viewMode === 'compact' ? 'p-1' : 'p-2'}`}
                              style={{ minWidth: viewMode === 'compact' ? '80px' : '120px' }}
                            >
                              {renderInterviewCell(timeSlot, companyId)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
          
          {/* Unscheduled Students Section */}
          <Card className="mb-4">
            <Card.Header as="h5" className="bg-warning text-dark">
              Unscheduled Shortlisted Students
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status" variant="primary" />
                  <p className="mt-2">Loading unscheduled students...</p>
                </div>
              ) : unscheduledStudents.length === 0 ? (
                <Alert variant="success">
                  All shortlisted students have been scheduled for interviews.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Student</th>
                        <th>Email</th>
                        <th>Preference</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCompanies.map(companyId => {
                        const companyUnscheduled = getUnscheduledForCompany(companyId);
                        if (companyUnscheduled.length === 0) return null;
                        
                        const company = companies.find(c => c.id === parseInt(companyId));
                        
                        return companyUnscheduled.map((item, index) => (
                          <tr key={`unscheduled-${companyId}-${index}`}>
                            <td>{company?.name || item.company.name}</td>
                            <td>{item.student.name}</td>
                            <td>{item.student.email}</td>
                            <td className="text-center">
                              <Badge bg="info">{item.preference_order}</Badge>
                            </td>
                            <td>
                              <small className="text-danger">{item.reason}</small>
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
          
          {conflicts.length > 0 && (
            <Card className="mb-4">
              <Card.Header as="h5" className="bg-danger text-white">
                Schedule Conflicts
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Conflict Type</th>
                      <th>Time</th>
                      <th>Companies</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.map((conflict, index) => (
                      <tr key={`conflict-${index}`}>
                        <td>
                          {conflict.student ? 'Student Double-booking' : 'Panel Double-booking'}
                        </td>
                        <td>
                          {conflict.time_slot ? 
                            `${formatTime(conflict.time_slot.start_time)} - ${formatTime(conflict.time_slot.end_time)}` : 
                            'N/A'
                          }
                        </td>
                        <td>
                          {conflict.interviews ? 
                            conflict.interviews.map(i => i.company?.name || 'Unknown').join(' & ') : 
                            (conflict.company?.name || 'Unknown')
                          }
                        </td>
                        <td>
                          {conflict.student ? (
                            <>
                              <strong>{conflict.student.name}</strong> is scheduled for multiple interviews at the same time
                            </>
                          ) : conflict.panel ? (
                            <>
                              <strong>Panel {conflict.panel.number}</strong> at <strong>{conflict.company?.name || 'Unknown Company'}</strong> has multiple students scheduled
                            </>
                          ) : (
                            'Conflict details unavailable'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      ) : (
        <Alert variant="info">
          Please select at least one company to view the schedule.
        </Alert>
      )}
      

      <InterviewDetailsModal 
        show={showInterviewModal}
        handleClose={() => setShowInterviewModal(false)}
        interview={selectedInterview}
        formatTime={formatTime}
        formatDate={formatDate}
      />
    </Container>
  );
};

export default GlobalSchedulingPage;